<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { ECOMMERCE_ITEM_PRICE_TYPE, type EcommerceItem } from '@/types/shared/player-config'
import { useEcommerceItemDetailsPurchase } from '@/composables/game/ecommerce'
import BaseButton from '@/components/base/BaseButton.vue'
import { T } from '@tolgee/vue'
import ThePremium from '@/components/ThePremium.vue'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import type { EcommercePrizeCardItem } from '@/types/shop'
import { ref } from 'vue'
import type { Skin } from '@/types/skin'
import { formatUnshortenedNumber } from '@/helpers/utils'
import TonDiscountBadge from '@/components/TonDiscountBadge.vue'

const props = defineProps<{
  show: boolean
  skin: Skin
  item: EcommerceItem | EcommercePrizeCardItem
}>()
const emit = defineEmits<{
  (e: 'hide'): void
  (e: 'onPurchase'): void
}>()

const {
  starPrice,
  tonPrice,
  isBuyingWithStars,
  isBuyingWithTon,
  showTonPurchase,
  onPurchase,
  selectedPriceType,
} = useEcommerceItemDetailsPurchase(props.item)

const isLoading = ref(false)

async function _onPurchase(priceType: ECOMMERCE_ITEM_PRICE_TYPE) {
  isLoading.value = true
  try {
    const paid = await onPurchase(priceType)
    if (paid) {
      setTimeout(() => {
        emit('onPurchase')
        isLoading.value = false
      }, priceType === ECOMMERCE_ITEM_PRICE_TYPE.STAR ? 350 : 1)
    } else {
      isLoading.value = false
      selectedPriceType.value = null
    }
  } catch (e) {
    isLoading.value = false
    selectedPriceType.value = null
  }
}

</script>

<template>
  <TheBottomSheet
    v-if="item"
    :show="show"
    @hide="$emit('hide')"
  >
    <div class="w-full flex flex-col items-center gap-y-12 relative">
      <StarsBalancePill
        class="absolute left-0 -top-6"
      />
      <div class="flex flex-col items-center gap-y-6">
        <div class="h-[88px] w-auto">
          <img
            :src="skin.imageUrl"
            class="object-contain max-w-full max-h-full"
          >
        </div>
        <div class="flex text-center max-w-[360px] text-2xl font-bold -tracking-[0.48px] text-light-white">
          {{ skin.name }} = ${{ starPrice.usdAmount }}
        </div>
      </div>
      <div class="w-full flex flex-col gap-y-3">
        <BaseButton
          v-if="starPrice.amount === 0"
          class="w-full"
          :loading="isBuyingWithStars || (isLoading && selectedPriceType === ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
          :class="(isBuyingWithStars || isLoading) && 'pointer-events -none'"
          @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
        >
          <T
            v-if="isBuyingWithStars || (isLoading && selectedPriceType === ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
            key-name="lingo.giveaway.transaction.loading_cta"
            default-value="Processing..."
          />
          <T
            v-else
            key-name="shop.unlock_for_free"
            default-value="Unlock for free"
          />
        </BaseButton>
        <BaseButton
          v-else
          class="w-full"
          :loading="isBuyingWithStars"
          @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
        >
          <span
            v-if="!isBuyingWithStars"
            class="flex items-center gap-x-2"
          >
            <T
              key-name="starter_pack.buy_with"
              default-value="Buy with"
            />
            <ThePremium :with-tooltip="false" />
            <span class="-ml-0.5">{{ formatUnshortenedNumber(starPrice.amount) }}</span>
          </span>
          <T
            v-else
            key-name="lingo.giveaway.transaction.loading_cta"
            default-value="Processing..."
          />
        </BaseButton>

        <template v-if="showTonPurchase">
          <div class="relative w-full">
            <div class="absolute right-12 -top-1 rotate-[20deg]">
              <TonDiscountBadge label="-10%" />
            </div>
            <BaseButton
              style-type="tertiary"
              :loading="isBuyingWithTon"
              class="w-full"
              @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.TON)"
            >
              <span
                v-if="!isBuyingWithTon"
                class="flex items-center gap-x-2"
              >
                <T
                  key-name="starter_pack.buy_with"
                  default-value="Buy with"
                />
                <img
                  src="@/assets/images/ton-symbol.png"
                  class="size-5 min-w-[20px] object-cover"
                >
                <span>{{ tonPrice?.amount }}</span>
              </span>
              <T
                v-else
                key-name="lingo.giveaway.transaction.loading_cta"
                default-value="Processing..."
              />
            </BaseButton>
          </div>

          <div class="mx-auto -mt-1 max-w-[307px] text-center text-sm -tracking-[0.28px] text-white/[.87] font-medium">
            <T
              key-name="transaction.process_description"
              default-value="Transaction may take a few minutes to process. You’ll be notified once it’s successful."
            />
          </div>
        </template>
      </div>
    </div>
  </TheBottomSheet>
</template>
